import * as React from 'react';
import 'react-dates/initialize';

import AppRouter from '@/AppRouter';
import { stores, StoresContext } from '@/store';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faArchive as faArchiveSolid,
  faCalendar,
  faCheckCircle as faCheckCircleSolid,
  faDollarSign,
  faDownload as faDownloadSolid,
  faEnvelope as faEnvelopeSolid,
  faExclamation,
  faEye as faEyeSolid,
  faFileSignature as faFileSignatureSolid,
  faPhone,
  faPlusSquare,
  faRss,
  faSave,
  faSignOutAlt,
  faSpinner as faSpinnerSolid,
  faSync as faSyncSolid,
  faTimes,
  faTimesCircle,
  faTrashRestore as faTrashRestoreSolid,
  faTrash as faTrashSolid,
  faUserClock,
  faUserPlus as faUserPlusSolid,
  faUserTimes as faUserTimesSolid,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBell,
  faBooks,
  faBracketsCurly,
  faBriefcaseMedical,
  faBuilding,
  faCalendarAlt,
  faCalendarCheck,
  faChartBar,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faClipboardListCheck,
  faClock,
  faCloudUpload,
  faCogs,
  faExchange,
  faExchangeAlt,
  faExclamationCircle,
  faFileCheck as faFileCheckLight,
  faFileExcel as faFileExcelLight,
  faFileImage as faFileImageLight,
  faFileMinus as faFileMinusLight,
  faFilePdf,
  faFileTimes as faFileTimesLight,
  faFileUpload as faFileUploadLight,
  faFileWord as faFileWordLight,
  faHome,
  faLongArrowDown,
  faLongArrowLeft,
  faMoneyCheckAlt,
  faPlus as faPlusLight,
  faSearch,
  faStopwatch,
  faUser,
  faUserCog,
  faUserPlus as faUserPlusLight,
  faUsersCog,
} from '@fortawesome/pro-light-svg-icons';
import {
  faChartBar as faChartBarRegular,
  faCheckCircle,
  faCheck as faCheckRegular,
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faChevronRight as faChevronRightRegular,
  faClipboard,
  faDesktop,
  faDownload as faDownloadRegular,
  faExclamationTriangle,
  faEye,
  faFile,
  faFileImage as faFileImageRegular,
  faFileMinus,
  faFilePdf as faFilePdfRegular,
  faFileSignature,
  faFileTimes as faFileTimesRegular,
  faFileWord as faFileWordRegular,
  faInfoCircle,
  faMinus,
  faMinusCircle,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faDiamond } from '@fortawesome/pro-solid-svg-icons';
import * as Sentry from '@sentry/react';
import { capitalize } from 'lodash';
import { Provider } from 'mobx-react';
import { useEffect } from 'react';
import DefaultTheme from 'react-dates/lib/theme/DefaultTheme';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Route, Routes, useLocation } from 'react-router';
import { useWindowSize } from 'react-use';
import aphroditeInterface from 'react-with-styles-interface-aphrodite';
import ThemedStyleSheet from 'react-with-styles/lib/ThemedStyleSheet';
import { LoginForm, Pane } from './components/LoginForm';
import ScreenSizeMessage from './components/ScreenSizeMessage';
import './sentry';

library.add(
  faBracketsCurly,
  faBriefcaseMedical,
  faBuilding,
  faCalendar,
  faCalendarAlt,
  faCalendarCheck,
  faCheck,
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faChevronLeft,
  faChevronRight,
  faClipboard,
  faClipboardListCheck,
  faClock,
  faCogs,
  faDesktop,
  faDiamond,
  faDollarSign,
  faExchange,
  faExchangeAlt,
  faExclamation,
  faExclamationCircle,
  faExclamationTriangle,
  faHome,
  faInfoCircle,
  faLongArrowDown,
  faLongArrowLeft,
  faMoneyCheckAlt,
  faPhone,
  faPlus,
  faPlusSquare,
  faRss,
  faSave,
  faSearch,
  faSignOutAlt,
  faStopwatch,
  faTimes,
  faTimesCircle,
  faTrash,
  faUser,
  faUserClock,
  faUserCog,
  faUsersCog,
  faBooks,
  faFile,
  faCloudUpload,
  faFilePdf,
  faCheckRegular,
  faEye,
  faCheckCircleSolid,
  faChevronRightRegular,
  faBell,
  faCheckCircle,
  faChartBarRegular,
  faChartBar,
  faUserPlusLight,
  faPlusLight,
  faFilePdfRegular,
  faFileWordRegular,
  faFileImageRegular,
  faFileTimesRegular,
  faDownloadRegular,
  faUserPlusSolid,
  faEnvelopeSolid,
  faUserTimesSolid,
  faFileWordLight,
  faFileImageLight,
  faFileMinusLight,
  faFileMinus,
  faMinus,
  faMinusCircle,
  faDownloadSolid,
  faFileSignature,
  faSpinnerSolid,
  faEyeSolid,
  faFileExcelLight,
  faSyncSolid,
  faFileSignatureSolid,
  faTrashSolid,
  faFileTimesLight,
  faFileUploadLight,
  faFileCheckLight,
  faArchiveSolid,
  faTrashRestoreSolid,
);

ThemedStyleSheet.registerInterface(aphroditeInterface);
ThemedStyleSheet.registerTheme({
  reactDates: {
    ...DefaultTheme.reactDates,
    zIndex: 2,
    border: {
      ...DefaultTheme.reactDates.border,
      input: {
        ...DefaultTheme.reactDates.border.input,
        border: '1px solid rgba(34,36,38,.15)',
        borderTop: '1px solid rgba(34,36,38,.15)',
        borderRight: '1px solid rgba(34,36,38,.15)',
        borderBottom: '1px solid rgba(34,36,38,.15)',
        borderLeft: '1px solid rgba(34,36,38,.15)',
        borderFocused: '1px solid #85b7d9',
        borderTopFocused: '1px solid #85b7d9',
        borderLeftFocused: '1px solid #85b7d9',
        borderBottomFocused: '1px solid #85b7d9',
        borderRightFocused: '1px solid #85b7d9',
        borderRadius: '4px',
      },
    },
    font: {
      ...DefaultTheme.reactDates.font,
      input: {
        ...DefaultTheme.reactDates.font.input,
        size: '0.9rem',
      },
    },
    color: {
      ...DefaultTheme.reactDates.color,
      selected: {
        ...DefaultTheme.reactDates.color.selected,
        backgroundColor: '#D7AB73',
        backgroundColor_active: '#D7AB73',
        backgroundColor_hover: '#D7AB73',
        borderColor: '#D7AB73',
        borderColor_active: '#D7AB73',
        borderColor_hover: '#D7AB73',
      },
      disabled: '#ddd',
    },
    spacing: {
      ...DefaultTheme.reactDates.spacing,
      captionPaddingBottom: 42,
    },
  },
});

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const App = () => {
  const { pathname } = useLocation();
  const { width } = useWindowSize();

  const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      document.title = `${capitalize(process.env.NODE_ENV)} - Masterpiece | Scheduling Portal`;
    }
  }, []);

  return (
    <StoresContext.Provider value={stores}>
      <Provider {...stores}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {width > 768 ? (
          <AppRouter />
        ) : pathname.includes('login') || pathname.includes('password') ? (
          <SentryRoutes>
            <Route path="/login" element={<LoginForm initialPane={Pane.Login} />} />
            <Route path="/password-reset" element={<LoginForm initialPane={Pane.FinishReset} />} />
          </SentryRoutes>
        ) : (
          <ScreenSizeMessage />
        )}
      </Provider>
    </StoresContext.Provider>
  );
};

export default App;
